import type { ScenarioConfig } from '../../engine/plant/types';

const H = 3600;

/**
 * Campaign scenarios for the water treatment plant. Each is a shift with a brief, a
 * disturbance and objectives; `steady` is the baseline the 8-hour stability test runs.
 */
export const scenarios: ScenarioConfig[] = [
  {
    id: 'steady',
    name: 'Steady shift',
    category: 'sandbox',
    brief:
      'Design conditions, everything in AUTO. Nothing is wrong and nothing is going to go ' +
      'wrong. Use it to learn the board, run step tests, and see what a well-behaved plant ' +
      'looks like.',
    durationS: 8 * H,
    events: [],
    objectives: [
      { id: 'spec', text: 'Keep final turbidity below 0.3 NTU', kind: 'maintain', condition: { op: '<', tag: 'AT-405.PV', value: 0.3 }, toleranceS: 60, weight: 3 },
      { id: 'ct', text: 'Keep the CT ratio above 1.0', kind: 'maintain', condition: { op: '>', tag: 'CT-601.CTRATIO', value: 1.0 }, toleranceS: 60, weight: 3 },
      { id: 'level', text: 'Keep the clearwell between 25 % and 92 %', kind: 'maintain', condition: { op: 'between', tag: 'LT-501.PV', lo: 25, hi: 92 }, toleranceS: 120, weight: 2 },
    ],
    stars: [55, 75, 92],
    tip: 'A steady plant is a boring plant. If you find yourself busy here, something is mistuned.',
  },

  {
    id: 'storm',
    name: 'Storm turbidity spike',
    category: 'campaign',
    brief:
      'Heavy rain overnight in the upper catchment. Raw turbidity is about to climb from ' +
      '8 NTU toward 150 NTU over the next hour. The coagulant demand rises with the square ' +
      'root of turbidity — the feed-forward will follow it, but the clarifier is going to be ' +
      'pushed hard and the filters will load up much faster than usual.',
    durationS: 8 * H,
    events: [
      { id: 'storm', at: 0.5 * H, effects: [{ do: 'fault', id: 'STORM', on: true }], announce: 'Catchment gauge: raw turbidity rising sharply.', hint: 'Reducing plant rate lengthens clarifier residence time and settles more solids. The clearwell can carry you for a couple of hours.' },
      { id: 'ease', at: 5 * H, effects: [{ do: 'fault', id: 'STORM', on: false }], announce: 'Raw turbidity easing.' },
    ],
    objectives: [
      { id: 'spec', text: 'Keep final turbidity below 0.3 NTU', kind: 'maintain', condition: { op: '<', tag: 'AT-405.PV', value: 0.3 }, toleranceS: 600, weight: 4 },
      { id: 'notrip', text: 'No plant trip', kind: 'avoid', condition: { op: 'tripped', id: 'I-405' }, weight: 3 },
      { id: 'supply', text: 'Never lose distribution pressure', kind: 'maintain', condition: { op: '>', tag: 'PT-701.PV', value: 3.5 }, toleranceS: 120, weight: 3 },
    ],
    stars: [50, 72, 88],
    tip: 'Optimum alum dose scales with √turbidity, not turbidity. Twenty times the turbidity needs about four and a half times the dose — no more.',
  },

  {
    id: 'peak-backwash',
    name: 'Evening peak with a filter down',
    category: 'campaign',
    brief:
      'Filter 3 has reached terminal headloss and has to be washed. The evening demand peak ' +
      'starts in an hour. Wash it, keep the remaining three filters inside their loading ' +
      'limit, and do not run the clearwell down far enough to trip the high-lift pumps.',
    durationS: 6 * H,
    events: [
      { id: 'dirty', at: 60, effects: [{ do: 'setTag', tag: 'F-403.HL', value: 2.3 }], announce: 'Filter 3 headloss has reached 2.3 m.' },
      { id: 'peak', at: 1 * H, effects: [{ do: 'fault', id: 'DEMAND-SURGE', on: true }], announce: 'Evening demand climbing.', hint: 'Backwash draws about 190 m³ from the clearwell and takes the filter out for half an hour. Start it before the peak, not during it.' },
      { id: 'peak-off', at: 4 * H, effects: [{ do: 'fault', id: 'DEMAND-SURGE', on: false }], announce: 'Demand falling back.' },
    ],
    objectives: [
      { id: 'wash', text: 'Backwash filter 3 and return it to service', kind: 'achieve', condition: { op: '<', tag: 'PDT-403.PV', value: 0.6 }, weight: 3 },
      { id: 'level', text: 'Never let the clearwell fall below 25 %', kind: 'maintain', condition: { op: '>', tag: 'LT-501.PV', value: 25 }, toleranceS: 60, weight: 3 },
      { id: 'spec', text: 'Keep final turbidity below 0.3 NTU', kind: 'maintain', condition: { op: '<', tag: 'AT-405.PV', value: 0.3 }, toleranceS: 300, weight: 3 },
      { id: 'notrip', text: 'No high-lift trip', kind: 'avoid', condition: { op: 'tripped', id: 'I-501' }, weight: 4 },
    ],
    stars: [50, 72, 90],
    tip: 'Three filters carrying 500 m³/h is 6.7 m/h each — above the 5 m/h design rate, so headloss builds a third faster while the fourth is out.',
  },

  {
    id: 'analyzer-drift',
    name: 'The analyzer that lied',
    category: 'campaign',
    brief:
      'Routine shift. Watch your instruments as carefully as your process — a transmitter ' +
      'that reads wrong is more dangerous than one that reads nothing, because the control ' +
      'system believes it.',
    durationS: 8 * H,
    events: [
      { id: 'drift', at: 1 * H, effects: [{ do: 'fault', id: 'AT601-DRIFT', on: true }], hint: 'The chlorine analyzer is drifting low at about 4 % of span per hour. AIC-601 is dosing to a lie.' },
    ],
    objectives: [
      { id: 'nooverdose', text: 'Keep the true chlorine residual below 4 mg/L', kind: 'maintain', condition: { op: '<', tag: 'CT-601.CL2', value: 4 }, toleranceS: 300, weight: 5 },
      { id: 'ct', text: 'Keep the CT ratio above 1.0', kind: 'maintain', condition: { op: '>', tag: 'CT-601.CTRATIO', value: 1.0 }, toleranceS: 120, weight: 3 },
      { id: 'chem', text: 'Do not waste chlorine', kind: 'maintain', condition: { op: '<', tag: 'PD-601.LPH', value: 12 }, toleranceS: 600, weight: 2 },
    ],
    stars: [45, 70, 88],
    tip: 'When a controller keeps pushing its output in one direction and the process will not respond, suspect the measurement before the process.',
  },

  {
    id: 'pump-trip',
    name: 'Intake pump trip',
    category: 'campaign',
    brief:
      'Both intake pumps are running at part speed. If one trips, the other has to make up ' +
      'the whole plant rate — and it may not have the head to do it.',
    durationS: 5 * H,
    events: [
      { id: 'trip', at: 45 * 60, effects: [{ do: 'fault', id: 'P101A-TRIP', on: true }], announce: 'Intake pump A has tripped on motor protection.', hint: 'FIC-101 will drive pump B harder. Check whether it reaches the setpoint — if not, the clearwell is your buffer and you should manage demand.' },
    ],
    objectives: [
      { id: 'level', text: 'Never let the clearwell fall below 25 %', kind: 'maintain', condition: { op: '>', tag: 'LT-501.PV', value: 25 }, toleranceS: 60, weight: 4 },
      { id: 'pressure', text: 'Hold distribution pressure above 3.5 barg', kind: 'maintain', condition: { op: '>', tag: 'PT-701.PV', value: 3.5 }, toleranceS: 60, weight: 3 },
      { id: 'ct', text: 'Keep the CT ratio above 1.0', kind: 'maintain', condition: { op: '>', tag: 'CT-601.CTRATIO', value: 1.0 }, toleranceS: 120, weight: 2 },
    ],
    stars: [50, 74, 90],
    tip: 'A single pump on the same curve delivers less than two — the system resistance rises with the square of the flow it has to carry alone.',
  },

  {
    id: 'breakthrough',
    name: 'One run too many',
    category: 'campaign',
    brief:
      'Filter 4 was left in service through the night shift and is well past its solids ' +
      'capacity. Headloss will warn you first; turbidity breakthrough follows a few hours ' +
      'later and is a regulatory event.',
    durationS: 6 * H,
    events: [
      { id: 'setup', at: 30, effects: [{ do: 'setTag', tag: 'F-404.HL', value: 2.25 }], announce: 'Night shift handover: filter 4 has been on line for 44 hours.' },
    ],
    objectives: [
      { id: 'spec', text: 'Keep final turbidity below 0.3 NTU', kind: 'maintain', condition: { op: '<', tag: 'AT-405.PV', value: 0.3 }, toleranceS: 120, weight: 5 },
      { id: 'wash', text: 'Get filter 4 washed and back in service', kind: 'achieve', condition: { op: '<', tag: 'PDT-404.PV', value: 0.6 }, weight: 3 },
      { id: 'supply', text: 'Keep supplying the network', kind: 'maintain', condition: { op: '>', tag: 'PT-701.PV', value: 3.5 }, toleranceS: 120, weight: 2 },
    ],
    stars: [50, 75, 92],
    tip: 'Headloss is a warning, turbidity is a failure. Act on the first and you never see the second.',
  },

  {
    id: 'coag-fail',
    name: 'Coagulant pump failure',
    category: 'campaign',
    brief:
      'The alum dosing pump is about to stop. Without coagulation, the clarifier removes ' +
      'almost nothing and the filters take the entire raw solids load. You have roughly the ' +
      'clarifier residence time — two hours — before it reaches the filters.',
    durationS: 6 * H,
    events: [
      { id: 'fail', at: 40 * 60, effects: [{ do: 'fault', id: 'PD201-FAIL', on: true }], announce: 'Coagulant dosing pump low-flow alarm.', hint: 'You cannot treat without coagulant. Cut the plant rate hard and live off the clearwell until dosing is restored.' },
      { id: 'fixed', at: 3 * H, effects: [{ do: 'fault', id: 'PD201-FAIL', on: false }], announce: 'Maintenance report the standby dosing pump is on line.' },
    ],
    objectives: [
      { id: 'spec', text: 'Keep final turbidity below 0.3 NTU', kind: 'maintain', condition: { op: '<', tag: 'AT-405.PV', value: 0.3 }, toleranceS: 600, weight: 5 },
      { id: 'supply', text: 'Never lose distribution pressure', kind: 'maintain', condition: { op: '>', tag: 'PT-701.PV', value: 3.5 }, toleranceS: 120, weight: 3 },
      { id: 'notrip', text: 'No plant trip', kind: 'avoid', condition: { op: 'tripped', id: 'I-405' }, weight: 2 },
    ],
    stars: [45, 70, 88],
    tip: 'Throughput is the one variable you always control. When treatment capacity falls, the rate has to fall with it.',
  },

  {
    id: 'stiction',
    name: 'A valve that will not sit still',
    category: 'campaign',
    brief:
      'Filter 2 is cycling. The flow will not settle, the valve travel is enormous, and the ' +
      'obvious answer — retune the controller — is the wrong one. Find out what is really ' +
      'happening before you touch the tuning.',
    durationS: 4 * H,
    events: [
      { id: 'stick', at: 20 * 60, effects: [{ do: 'fault', id: 'FV402-STICTION', on: true }], hint: 'Compare FIC-402 OP with FV-402 position. A square-wave OP against a stepping position is stiction, not tuning.' },
    ],
    objectives: [
      { id: 'iae', text: 'Keep the other three filters steady', kind: 'maintain', condition: { op: 'between', tag: 'FT-401.PV', lo: 100, hi: 200 }, toleranceS: 300, weight: 2 },
      { id: 'spec', text: 'Keep final turbidity below 0.3 NTU', kind: 'maintain', condition: { op: '<', tag: 'AT-405.PV', value: 0.3 }, toleranceS: 300, weight: 3 },
      { id: 'travel', text: 'Stop the limit cycle — take FIC-402 out of automatic if you must', kind: 'achieve', condition: { op: 'mode', loop: 'FIC-402', is: 'MAN' }, weight: 3 },
    ],
    stars: [45, 70, 88],
    tip: 'Integral action plus a sticking valve is a guaranteed limit cycle. Reducing the gain slows the cycle; it never stops it. The valve needs a fitter.',
  },

  {
    id: 'cold-start',
    name: 'Cold start',
    category: 'campaign',
    brief:
      'The plant is down and the clearwell is low. Bring it up: start the intake pumps, ' +
      'establish coagulation, get the filters onto rate, prove the chlorine residual and ' +
      'the CT credit, then start distributing. Nothing is in automatic.',
    durationS: 6 * H,
    initial: 'cold',
    events: [],
    objectives: [
      { id: 'flow', text: 'Establish 450 m³/h or more of raw water flow', kind: 'achieve', condition: { op: '>', tag: 'FT-101.PV', value: 450 }, weight: 2 },
      { id: 'auto', text: 'Get FIC-101 into cascade on the clearwell level', kind: 'achieve', condition: { op: 'mode', loop: 'FIC-101', is: 'CAS' }, weight: 2 },
      { id: 'level', text: 'Recover the clearwell to 60 %', kind: 'achieve', condition: { op: '>', tag: 'LT-501.PV', value: 60 }, weight: 3 },
      { id: 'ct', text: 'Achieve a CT ratio above 1.0', kind: 'achieve', condition: { op: '>', tag: 'CT-601.CTRATIO', value: 1.0 }, weight: 3 },
      { id: 'spec', text: 'Never distribute water above 0.3 NTU', kind: 'maintain', condition: { op: 'or', terms: [{ op: '<', tag: 'AT-405.PV', value: 0.3 }, { op: '<', tag: 'S-DIST.Q', value: 5 }] }, toleranceS: 120, weight: 3 },
    ],
    stars: [45, 70, 90],
    tip: 'Fill the train from the front. Water has to travel the whole plant before anything downstream tells you the truth.',
  },
];

/** Tuning challenges: a fixed sequence of upsets, scored on integrated absolute error. */
export const tuningChallenges: ScenarioConfig[] = [
  {
    id: 'tune-pic701',
    name: 'Tuning challenge — distribution pressure',
    category: 'tuning',
    challengeLoop: 'PIC-701',
    challengeTarget: 900,
    brief:
      'A fast loop on a fast process: high-lift pump speed against distribution pressure. ' +
      'Four setpoint changes and a demand surge, thirty minutes, and the score is the ' +
      'integrated absolute error on PIC-701. Tune it however you like before you start — the ' +
      'clock and the error counter start together.',
    durationS: 30 * 60,
    events: [
      { id: 'sp1', at: 120, effects: [{ do: 'setSp', loop: 'PIC-701', value: 4.9 }] },
      { id: 'sp2', at: 420, effects: [{ do: 'setSp', loop: 'PIC-701', value: 4.2 }] },
      { id: 'surge', at: 780, effects: [{ do: 'fault', id: 'DEMAND-SURGE', on: true }], announce: 'Demand surge.' },
      { id: 'sp3', at: 1080, effects: [{ do: 'setSp', loop: 'PIC-701', value: 4.7 }] },
      { id: 'calm', at: 1380, effects: [{ do: 'fault', id: 'DEMAND-SURGE', on: false }] },
      { id: 'sp4', at: 1500, effects: [{ do: 'setSp', loop: 'PIC-701', value: 4.5 }] },
    ],
    objectives: [
      { id: 'iae', text: 'Keep the integrated absolute error below 900 %·s', kind: 'maintain', condition: { op: '<', tag: 'PIC-701.IAE', value: 900 }, toleranceS: 0, weight: 5 },
      { id: 'travel', text: 'Do not thrash the pumps — keep the output out of its limits', kind: 'avoid', condition: { op: 'for', term: { op: '>', tag: 'PIC-701.OP', value: 99 }, seconds: 30 }, weight: 2 },
    ],
    stars: [50, 74, 92],
    tip: 'A flow-like loop wants a small gain and a short integral time. Derivative on a noisy pressure signal will cost you more in valve travel than it saves in error.',
  },
  {
    id: 'tune-aic601',
    name: 'Tuning challenge — chlorine residual',
    category: 'tuning',
    challengeLoop: 'AIC-601',
    challengeTarget: 2600,
    brief:
      'The opposite problem: twenty minutes of transport delay and ninety seconds of analyser ' +
      'dead time. Three setpoint changes over three hours, scored on the integrated absolute ' +
      'error. There is a hard limit to how tightly this can be tuned, and finding it is the ' +
      'exercise.',
    durationS: 3 * 3600,
    events: [
      { id: 'sp1', at: 15 * 60, effects: [{ do: 'setSp', loop: 'AIC-601', value: 1.4 }] },
      { id: 'sp2', at: 60 * 60, effects: [{ do: 'setSp', loop: 'AIC-601', value: 0.8 }] },
      { id: 'flow', at: 105 * 60, effects: [{ do: 'fault', id: 'DEMAND-SURGE', on: true }], announce: 'Demand surge — the flow feed-forward will be tested.' },
      { id: 'sp3', at: 140 * 60, effects: [{ do: 'setSp', loop: 'AIC-601', value: 1.1 }] },
    ],
    objectives: [
      { id: 'iae', text: 'Keep the integrated absolute error below 2600 %·s', kind: 'maintain', condition: { op: '<', tag: 'AIC-601.IAE', value: 2600 }, toleranceS: 0, weight: 5 },
      { id: 'stable', text: 'No sustained cycling — keep the residual within 0.5 mg/L of setpoint', kind: 'maintain', condition: { op: '<', tag: 'AIC-601.ERR', value: 0.5 }, toleranceS: 900, weight: 2 },
    ],
    stars: [50, 74, 92],
    tip: 'With θ ≈ τ, no tuning will make this loop fast. The win is in the feed-forward: a dose calculation that is right leaves the feedback almost nothing to do.',
  },
];
